import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import CheckBoxStateChange from '@app/models/check-box-state-change';
import { LogService, ValueService } from '@app/services';
import { ICheckboxConfiguration } from '@app/interfaces';
import { ActivatedRoute, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { ValueConversionService } from '@oa/shared-core';

/**
 *
             <oa-checkbox
              id="enable-hipcamp-sync-checkbox"
              [isChecked]="syncHipcampListingJobEnabled"
              [label]="'Enable/Disable the SyncHipcampListingJobEnabled AppSetting Value'"
              (stateChange)="onCheckboxStateChange($event,'syncHipcampListingJobEnabled')"
            >
            </oa-checkbox>
 */
@Component({
    selector: "oa-checkbox",
    templateUrl: "./oa-checkbox.component.html",
    styleUrls: ["./oa-checkbox.component.less"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: []
})
export class OACheckboxComponent implements OnInit, OnDestroy {
  private readonly _componentName = 'OACheckboxComponent';
  private _label: string = '';
  private _checkedState: number = 0;
  private _config: ICheckboxConfiguration;
  /**
   * Private field for verbose logging
   */
  private _verboseLogging = false;

  /**
   * Private subscription for router events
   */
  private routerEvent$: Subscription;

  @Output() stateChange = new EventEmitter<CheckBoxStateChange>();

  @Output() isCheckedChange = new EventEmitter<boolean>();

  public title: string;

  @Input()
  public labelCss: string;

  constructor(
    private log: LogService,
    private valueConversionService: ValueConversionService,
    private router: Router,
    private route: ActivatedRoute,
    private valueService: ValueService
  ) {
    this.subscribeRouterEvents();
  }

  ngOnInit() {}

  /**
   * Called once, before the instance is destroyed.
   */
  ngOnDestroy(): void {
    if (this.routerEvent$) {
      this.routerEvent$.unsubscribe();
    }
  }

  /**
   * Gets the checkbox state
   */
  public get checkedState(): number {
    return this._checkedState;
  }

  /**
   * Sets the checkbox state
   */
  @Input()
  public set checkedState(v: number) {
    this._checkedState = v;
  }

  /**
   * A derived value from the checked state.  The checked state
   * can technically support more than boolean states, but typically that is
   * how it is used (and initially how this component is developed.)
   */
  public get isChecked(): boolean {
    return this.checkedState === 1;
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging || this._verboseLogging;
  }

  @Input()
  public set isChecked(v: boolean) {
    if (v) {
      const b = this.valueConversionService.getBoolean(v + '');

      if (b) {
        this.checkedState = 1;
      } else {
        this.checkedState = 0;
      }
    } else {
      this.checkedState = 0;
    }
  }

  public get label(): string {
    return this._label;
  }

  @Input()
  public set label(v: string) {
    this._label = v;
  }

  
  private _isDisabled : boolean;
  public get isDisabled() : boolean {
    return this._isDisabled;
  }

  @Input()
  public set isDisabled(v : boolean) {
    this._isDisabled = v;
  }
  

  public get config(): ICheckboxConfiguration {
    return this._config;
  }

  @Input()
  public set config(v: ICheckboxConfiguration) {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.config - set - v=`, v);
    }

    this._config = v;

    if (v) {
      this.checkedState = +!!v.checked;
      this.label = v.label;
      this.title = v.title;
    }
  }

  /**
   * Fires when the check box is tapped or clicked with the mouse.
   */
  public onClick() {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.onClick()`);
    }

    if (this.isDisabled){
      // TODO: maybe show an alert?
      return;
    }

    let change = {} as CheckBoxStateChange;

    change.oldValue = this.checkedState;

    // right now, we aren't supporting any state other 0/1

    if (this.checkedState === 0) {
      this.checkedState = 1;
    } else {
      this.checkedState = 0;
    }

    change.value = this.checkedState;

    if (this.config) {
      this.config.checked = this.checkedState === 1;
    }

    if (this.config && this.config.id) {
      change.id = String(this.config.id);
    }

    this.stateChange.emit(change);

    this.isCheckedChange.emit(this.checkedState === 1);
  }

  /**
   * Subscribes to Router Events and sets the _verboseLogging flag
   */
  private subscribeRouterEvents() {
    this.routerEvent$ = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        console.log(`${this._componentName}.CTOR router.events NavigationEnd:`, event);

        console.log(
          `${this._componentName}.CTOR router.events NavigationEnd route.snapshot.queryParamMap=`,
          this.route.snapshot.queryParamMap
        );

        if (this.route.snapshot.queryParamMap.has('verboseLogging')) {
          if (this.valueService.getBoolean(this.route.snapshot.queryParamMap.get('verboseLogging')) == true) {
            this._verboseLogging = true;
          } else {
            this._verboseLogging = false;
          }
        }
      }
    });
  }
}
